import styles from './ConsumerInfo.module.scss';
import { activateSnackbar } from 'store/features/ui';
import { useAppDispatch } from 'store';
import {
    FormAddress,
    FormInputGroup,
    FormProvider,
    FormSubmitButton,
    FormText,
} from 'components/form';
import Save from '@mui/icons-material/Save';
import * as yup from 'yup';
import { FieldValues } from 'react-hook-form';
import { useMemo, useState } from 'react';
import { FirebaseUser } from 'types/User/firebaseTypes';
import { updateConsumerUserInfo } from 'lib/firebase';

export interface ConsumerInfoProps {
    user: FirebaseUser;
}

const consumerInfoSchema = yup.object({
    name: yup.string().required(),
    address: yup.string(),
    email: yup.string().email(),
    phone: yup.string(),
    uid: yup.string(),
});

export interface ConsumerInfoData {
    name: string;
    address?: string;
    /** A readonly field. */
    email?: string;
    phone?: string;
    /** A readonly field. */
    uid: string;
}

export const ConsumerInfo = ({ user }: ConsumerInfoProps) => {
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);

    const onError = (error: FieldValues) => {
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: messages.join('\n'),
                severity: 'error',
            })
        );
    };

    const onSubmit = (data: ConsumerInfoData) => {
        setIsSaving(true);
        // Make network call to update consumer info.
        updateConsumerUserInfo(data).then(() => {
            setIsSaving(false);
            dispatch(
                activateSnackbar({
                    message: 'Saved Successfully',
                    severity: 'success',
                })
            );
        });
    };

    const defaultValues = useMemo(() => {
        return {
            name: user.info.name ?? '',
            address: user.info.address ?? '',
            email: user.info.email ?? '',
            phone: user.info.phone ?? '',
            uid: user.info.uid ?? '',
        };
    }, [user]);

    return (
        <div data-testid="ConsumerInfo" className={styles.ConsumerInfo}>
            <FormProvider
                defaultValues={defaultValues}
                validationSchema={consumerInfoSchema}
            >
                <FormInputGroup>
                    <FormText field="name" label="Name" />
                </FormInputGroup>
                <FormInputGroup>
                    <FormText disabled={true} field="email" label="Email" />
                </FormInputGroup>
                <FormInputGroup>
                    <FormAddress field="address" />
                </FormInputGroup>
                <FormInputGroup>
                    <FormText field="phone" label="Phone" />
                </FormInputGroup>
                <FormInputGroup>
                    <FormText disabled={true} field="uid" label="User ID" />
                </FormInputGroup>

                <div className={styles.saveButtonContainer}>
                    <FormSubmitButton
                        onValidHandler={(infoInput: ConsumerInfoData) => {
                            onSubmit(infoInput);
                        }}
                        onErrorHandler={onError}
                        buttonLabel="Save"
                        startIcon={<Save />}
                        isLoading={isSaving}
                    />
                </div>
            </FormProvider>
        </div>
    );
};
